import { Box, Card, Stack, ToggleButton, ToggleButtonGroup, Typography, useTheme } from "@mui/material";
import { LineChart, lineClasses } from "@mui/x-charts";
import { useState } from "react";
import { sumBy } from "@entrilia/shared/utilities/arrayHelper";
import { formatDate } from "@entrilia/shared/utilities/dateUtils";
import { pluralize } from "@entrilia/shared/utilities/stringHelper";
import {
  getViewsRangeStart,
  groupViewsByDay,
  viewsRangeLabels,
  viewsRanges,
  type ViewsRange,
} from "./investorCommunicationEngagement";

interface Props {
  viewedAt: string[];
}

const InvestorCommunicationViewsChart = ({ viewedAt }: Props) => {
  const theme = useTheme();
  const [range, setRange] = useState<ViewsRange>("allTime");
  const [today] = useState(() => new Date());

  const from = getViewsRangeStart(range, viewedAt, today);
  const dailyViews = groupViewsByDay(viewedAt, from, today);
  const total = sumBy(dailyViews, (dailyView) => dailyView.views);

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Stack>
            <Typography variant="subtitle2">Views over time</Typography>
            <Typography variant="caption" color="textSecondary">
              {total} {pluralize("view", total)} in this range
            </Typography>
          </Stack>
          <ToggleButtonGroup
            value={range}
            exclusive
            onChange={(_e, value: ViewsRange | null) => value && setRange(value)}
          >
            {viewsRanges.map((viewsRange) => (
              <ToggleButton key={viewsRange} value={viewsRange}>
                {viewsRangeLabels[viewsRange]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <LineChart
          height={260}
          xAxis={[
            {
              scaleType: "point",
              data: dailyViews.map((dailyView) => dailyView.date),
              valueFormatter: (date: Date) => formatDate(date, "MMM d"),
            },
          ]}
          yAxis={[{ tickMinStep: 1, min: 0, max: total === 0 ? 1 : undefined }]}
          series={[
            {
              data: dailyViews.map((dailyView) => dailyView.views),
              area: true,
              curve: "linear",
              showMark: dailyViews.length === 1,
              color: theme.palette.success.main,
              valueFormatter: (value) => `${value ?? 0} ${pluralize("view", value ?? 0)}`,
            },
          ]}
          grid={{ horizontal: true }}
          hideLegend
          sx={{ [`& .${lineClasses.area}`]: { fillOpacity: 0.12 } }}
        />
      </Stack>
    </Card>
  );
};

export default InvestorCommunicationViewsChart;
